export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
  };
}

const CSRF_KEY = "sedapsayur_csrf_token";
const MEMBER_CSRF_KEY = "sedapsayur_member_csrf_token";

function captureCsrf(value: unknown, key: string): void {
  if (value && typeof value === "object" && "csrfToken" in value) {
    const token = (value as { csrfToken?: unknown }).csrfToken;
    if (typeof token === "string") sessionStorage.setItem(key, token);
  }
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  retryInvalidCsrf = true,
): Promise<T> {
  const memberPath = path.startsWith("/api/member/");
  const csrfKey = memberPath ? MEMBER_CSRF_KEY : CSRF_KEY;
  const csrfToken = sessionStorage.getItem(csrfKey);
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (
    init.body &&
    !headers.has("Content-Type") &&
    !(init.body instanceof FormData)
  ) {
    headers.set("Content-Type", "application/json");
  }
  if (
    csrfToken &&
    init.method &&
    init.method !== "GET" &&
    init.method !== "HEAD"
  ) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers, credentials: "include" });
  } catch {
    throw new ApiError(
      "Tidak dapat terhubung. Periksa internet lalu coba lagi.",
      0,
      "NETWORK_ERROR",
    );
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => "");
  const envelope =
    payload && typeof payload === "object"
      ? (payload as ApiEnvelope<T>)
      : undefined;

  if (!response.ok || envelope?.success === false) {
    if (
      retryInvalidCsrf &&
      response.status === 403 &&
      envelope?.error?.code === "INVALID_CSRF" &&
      path !== "/api/auth/me" &&
      path !== "/api/member/me"
    ) {
      const refreshPath = memberPath ? "/api/member/me" : "/api/auth/me";
      const refreshResponse = await fetch(refreshPath, {
        headers: { Accept: "application/json" },
        credentials: "include",
      });
      if (refreshResponse.ok) {
        const refreshPayload = (await refreshResponse
          .json()
          .catch(() => null)) as ApiEnvelope<unknown> | null;
        captureCsrf(refreshPayload?.data, csrfKey);
        return apiRequest<T>(path, init, false);
      }
    }
    throw new ApiError(
      envelope?.error?.message || "Terjadi masalah. Silakan coba lagi.",
      response.status,
      envelope?.error?.code,
    );
  }

  const data =
    envelope && "data" in envelope ? (envelope.data as T) : (payload as T);
  captureCsrf(data, csrfKey);
  return data;
}

export function queryString(
  values: Record<string, string | number | undefined>,
): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const result = params.toString();
  return result ? `?${result}` : "";
}

export function jsonBody(value: unknown): Pick<RequestInit, "body"> {
  return { body: JSON.stringify(value) };
}
